import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
  Zoom,
  CircularProgress,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Popover } from "antd";
import { getHealthcareProvidersLookUp } from "store";
import CreateUpdatePhysicianModal from "./createUpdatePhysicianModal";
import decodedUserDetails from "utils/decodedUserDetails";
import { handleChangeSavedFields } from "store/slice/savedInputSlice";
import { handleChangeMultiFields } from "store/slice/multiStepFormInputSlice";
import { handleChangePhysicianFields } from "store/slice/physicianInputSlice";
import truncateString from "components/mnl/global/truncateString";
import { parsedClientDetails } from "utils/parsedClientDetails";
import useDebounce from "hooks/useDebounce";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const filter = createFilterOptions();

const FourthSection = () => {
  const [physicianData, setPhysicianData] = useState([]);
  const [isPhysicianNew, setIsPhysicianNew] = useState(false);
  const [selectedPhysicianId, setSelectedPhysicianId] = useState(null);
  const [showPhysicianEditModal, setShowPhysicianEditModal] = useState(false);
  const inputs = useSelector((state) => state.inputs);
  const savedInputs = useSelector((state) => state.savedInputs);
  const multiStepFormInputs = useSelector((state) => state.multiStepFormInputs);
  const separatedEmails = inputs.patientEmailForDisplay?.join(", ");
  const clientId = decodedUserDetails()?.clientId;
  const dispatch = useDispatch();
  const { clientDetails } = parsedClientDetails();
  const { user } = useSelector((state) => state.auth);
  const trimmedPhysicianName = savedInputs?.physicianName
    ?.replace(/\s+/g, " ")
    ?.trim();
  const debouncedSearchHealthcareProviderValue = useDebounce(
    trimmedPhysicianName,
    1000,
  );

  // Healthcare Provider infinite data loading options
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    clientId: clientId,
    pageSize: 10,
    pageNumber: 1,
  });
  const { itemRef: lastIcdItemRef } = useInfiniteScroll({
    loading,
    hasNextPage,
    callbackFn: () => {
      setFilterOptions((prev) => ({
        ...prev,
        pageNumber: prev.pageNumber + 1,
      }));
    },
  });

  const handleChange = (e) => {
    //Search filter of physician input fields
    dispatch(
      handleChangeSavedFields({
        physicianName: e.target.value,
      }),
    );
  };

  const handleOpenModal = () => {
    //Function for showing the create physician modal, this is implemented in physician's dropdown search input field when you type and auto suggests to add new patient
    setIsPhysicianNew(true);
  };

  const handleOpenEditModal = () => {
    //For showing the create or update physician modal
    if (multiStepFormInputs.providerId === null) {
      return setIsPhysicianNew(true);
    } else {
      return setShowPhysicianEditModal(true);
    }
  };

  const handleCloseModal = () => {
    //Function for closing physician modal
    setIsPhysicianNew(false);
    setShowPhysicianEditModal(false);
    setSelectedPhysicianId(null);
  };

  const handleSelectPhysician = (selectedPhysician) => {
    //For selecting a physician, and populating the fields that needs that information

    setSelectedPhysicianId(selectedPhysician.id);
    dispatch(
      handleChangeMultiFields({
        providerId: selectedPhysician.id,
      }),
    );
    dispatch(
      handleChangeSavedFields({
        physicianCode: selectedPhysician.id,
        physicianName: selectedPhysician?.displayName?.trim(),
        refetchData: true,
      }),
    );
    dispatch(
      handleChangePhysicianFields({
        id: selectedPhysician?.id,
        physicianFullName: selectedPhysician?.displayName,
      }),
    );
    setFilterOptions({
      ...filterOptions,
      pageSize: 10,
      pageNumber: 1,
    });
  };

  const handleFetchHealthcareProvidersLookup = () => {
    setLoading(true);
    //Fetching of healthcare providers
    dispatch(
      getHealthcareProvidersLookUp({
        ...filterOptions,
        search: multiStepFormInputs?.providerId
          ? ""
          : debouncedSearchHealthcareProviderValue,
      }),
    ).then((response) => {
      const hasEmptyData = response?.payload?.data?.items?.length === 0;
      const isFetchSuccessful = response?.payload?.success;
      if (isFetchSuccessful) {
        setPhysicianData(
          hasEmptyData
            ? []
            : (prev) =>
                [...prev, ...response?.payload?.data?.items]?.filter(
                  (value, index, self) =>
                    index === self?.findIndex((t) => t?.id === value?.id),
                ),
        );
        setHasNextPage(response?.payload?.data?.hasMore);
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    handleFetchHealthcareProvidersLookup(); //Invoking of get function
    // eslint-disable-next-line
  }, [
    debouncedSearchHealthcareProviderValue,
    filterOptions,
    savedInputs.refetchData,
  ]);

  useEffect(() => {
    if (savedInputs.physicianName === "") {
      //Check if physician input field has been cleared, then also clear input fields that uses physician
      setSelectedPhysicianId(null);
      dispatch(
        handleChangeMultiFields({
          providerId: null,
        }),
      );
      dispatch(
        handleChangePhysicianFields({
          id: 0,
          firstName: "",
          licenseNumber: "",
          licenseExpirationDate: null,
          lastName: "",
          title: "",
          specialty: "",
          phoneNumber: "",
          email: "",
        }),
      );
      dispatch(
        handleChangeSavedFields({
          physicianCode: 0,
          physicianName: "",
        }),
      );
      setFilterOptions({
        ...filterOptions,
        pageSize: 10,
        pageNumber: 1,
      });
    }
  }, [dispatch, savedInputs.physicianName]);

  return (
    <>
      <Grid rowSpacing={2} columnSpacing={4} sx={{ marginTop: 3 }} container>
        <Grid xs={12} sm={12} md={6} lg={4} xl={4} item>
          <Typography
            variant="p"
            sx={{ fontWeight: "bold", color: "darkBlue.main" }}
          >
            Bill type
          </Typography>
          <br />
          <TextField
            size="small"
            value={"Bill to Client"}
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
                md: "94%",
                lg: "94%",
                xl: "94%",
              },
              "& .MuiOutlinedInput-root": {
                "& > fieldset": {
                  border: "2px solid",
                  borderColor: "darkBlue.secondary",
                },
              },
              borderRadius: "5px",
              outline: "none",
              marginTop: "0.5rem",
            }}
            placeholder="Bill to client"
            id="outlined-basic"
            variant="outlined"
            inputProps={{
              sx: {
                "&::placeholder": {
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "black",
                },
                fontWeight: "bold",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowBackIosNewRoundedIcon
                    sx={{
                      transform: "rotate(-90deg)",
                      color: "lightBlue.main",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
            flexDirection: "column",
          }}
          xs={12}
          sm={12}
          md={6}
          lg={4}
          xl={4}
          item
        >
          <Typography
            sx={{
              color: "darkBlue.main",
              fontWeight: "600",
              fontSize: "0.9rem",
            }}
            variant="p"
          >
            Prepared by
          </Typography>
          <Typography
            sx={{
              fontWeight: "600",
              fontSize: "1.2rem",
              backgroundColor: "grayishBlue.main",
              width: "max-content",
              marginTop: "0.5rem",
              padding: "0.4rem",
            }}
            variant="h1"
          >
            {user?.displayName}
          </Typography>
        </Grid>
        <Grid
          sx={{ position: "relative" }}
          xs={12}
          sm={12}
          md={12}
          lg={4}
          xl={4}
          item
        >
          <Typography
            variant="p"
            sx={{ fontWeight: "bold", color: "darkBlue.main" }}
          >
            Physician name
          </Typography>
          <br />
          <Box
            sx={{
              display: "flex",
              gap: "0.3rem",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Autocomplete
              autoHighlight={
                savedInputs?.physicianName?.trim()?.length > 0 &&
                physicianData?.length > 0
              }
              sx={{
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "15rem",
                  lg: "15rem",
                  xl: "15rem",
                },
              }}
              disableClearable
              clearOnBlur
              size="small"
              value={trimmedPhysicianName}
              options={physicianData}
              renderOption={(props, physician) => (
                <>
                  <Box
                    sx={{
                      display: {
                        xs: "none",
                        sm: "none",
                        md: "block",
                        lg: "block",
                        xl: "block",
                      },
                    }}
                  >
                    <Popover
                      placement="rightTop"
                      autoAdjustOverflow
                      zIndex={99999}
                      content={`${physician.displayName}`}
                      title="Physician Details"
                    >
                      <li
                        style={{
                          margin: "0.2rem",
                          backgroundColor: physician?.displayName?.includes(
                            "Add new",
                          )
                            ? "#c1dbff"
                            : "",
                        }}
                        {...props}
                      >
                        {truncateString(physician.displayName)}
                      </li>
                    </Popover>
                  </Box>
                  <Box
                    sx={{
                      display: {
                        xs: "block",
                        sm: "block",
                        md: "none",
                        lg: "none",
                        xl: "none",
                      },
                    }}
                  >
                    <li
                      style={{
                        margin: "0.2rem",
                        backgroundColor: physician?.displayName?.includes(
                          "Add new",
                        )
                          ? "#c1dbff"
                          : "",
                      }}
                      {...props}
                    >
                      {physician.displayName.length > 22
                        ? physician.displayName.substring(0, 27) + "..."
                        : physician.displayName}
                    </li>
                  </Box>
                  {physicianData?.length === props["data-option-index"] + 1 &&
                    (loading || hasNextPage) && (
                      <li
                        ref={
                          physicianData?.length ===
                          props["data-option-index"] + 1
                            ? lastIcdItemRef
                            : undefined
                        }
                        className="flex items-center justify-center"
                      >
                        <CircularProgress size="20px" />
                      </li>
                    )}
                </>
              )}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  const modalOpenTimeout = setTimeout(() => {
                    handleOpenModal();
                  });
                  return () => clearTimeout(modalOpenTimeout);
                } else if (newValue && newValue.inputValue) {
                  handleOpenModal();
                } else {
                  handleSelectPhysician(newValue);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== "") {
                  filtered.push({
                    inputValue: params.inputValue,
                    displayName: "Add new physician *",
                  });
                }
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                if (option.inputValue) {
                  return option.inputValue;
                }
                if (option.displayName) {
                  return option.displayName;
                }
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "100%",
                      md: "15rem",
                      lg: "15rem",
                      xl: "15rem",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& > fieldset": {
                        border: "2px solid",
                        borderColor: "darkBlue.secondary",
                      },
                    },
                    borderRadius: "5px",
                    outline: "none",
                    marginTop: "0.5rem",
                  }}
                  onChange={handleChange}
                  value={trimmedPhysicianName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        debouncedSearchHealthcareProviderValue?.trim()?.length >
                          0 &&
                        physicianData?.length === 0
                      ) {
                        setIsPhysicianNew(true);
                      }
                    }
                  }}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <Box sx={{ overflow: "hidden", opacity: 0 }}>
                          {params.InputProps.endAdornment}
                        </Box>
                        <InputAdornment
                          sx={{ transform: "translateX(30px)" }}
                          position="end"
                        >
                          {!loading && !multiStepFormInputs?.providerId && (
                            <SearchOutlinedIcon
                              sx={{
                                color: "lightBlue.main",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                          {!loading && !!multiStepFormInputs?.providerId && (
                            <CloseRoundedIcon
                              className="cursor-pointer"
                              onClick={() => {
                                setSelectedPhysicianId(null);
                                dispatch(
                                  handleChangeMultiFields({
                                    providerId: null,
                                  }),
                                );
                                dispatch(
                                  handleChangePhysicianFields({
                                    id: 0,
                                    firstName: "",
                                    licenseNumber: "",
                                    licenseExpirationDate: null,
                                    lastName: "",
                                    title: "",
                                    specialty: "",
                                    phoneNumber: "",
                                    email: "",
                                  }),
                                );
                                dispatch(
                                  handleChangeSavedFields({
                                    physicianCode: 0,
                                    physicianName: "",
                                  }),
                                );
                                setFilterOptions({
                                  ...filterOptions,
                                  pageSize: 10,
                                  pageNumber: 1,
                                  search: "",
                                });
                              }}
                            />
                          )}
                          {loading && <CircularProgress size="20px" />}
                        </InputAdornment>
                      </>
                    ),
                  }}
                />
              )}
            />
            <Tooltip
              title={
                multiStepFormInputs.providerId === null
                  ? "Add new physician"
                  : "Edit physician's info"
              }
              placement="right-end"
              TransitionComponent={Zoom}
              arrow
            >
              <Box
                sx={{
                  backgroundColor: "darkBlue.main",
                  padding: "0.7rem",
                  height: "2.55rem",
                  borderRadius: "0.3rem",
                  cursor: "pointer",
                  marginTop: "0.5rem",
                  transition: "0.3s ease-in-out",
                  ":hover": {
                    backgroundColor: "darkBlue.secondary",
                  },
                }}
                onClick={handleOpenEditModal}
              >
                {multiStepFormInputs.providerId === null ? (
                  <PersonAddOutlinedIcon
                    sx={{
                      fontSize: "20px",
                      marginBottom: "0.1rem",
                      color: "white",
                      transform: "translateY(-3px)",
                    }}
                  />
                ) : (
                  <EditOutlinedIcon
                    sx={{
                      fontSize: "20px",
                      marginBottom: "0.3rem",
                      color: "white",
                      transform: "translateY(-3px)",
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
      {clientDetails?.clientConfig?.allowEmailSendingOfRequest && (
        <Grid rowSpacing={3} sx={{ marginTop: 3 }} container>
          <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
            <Typography
              variant="p"
              sx={{ fontWeight: "bold", color: "darkBlue.main" }}
            >
              Email address
            </Typography>
            <br />
            {inputs?.patientEmailForDisplay?.length > 0 && (
              <Typography
                variant="p"
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "grayishBlue.secondary",
                  padding: "0.25rem",
                  width: "100%",
                }}
              >
                {separatedEmails}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      <Modal
        closable
        onCancel={handleCloseModal}
        open={isPhysicianNew || showPhysicianEditModal}
        width={700}
        footer={null}
      >
        <CreateUpdatePhysicianModal
          showPhysicianEditModal={showPhysicianEditModal}
          handleCloseModal={handleCloseModal}
          selectedPhysicianId={selectedPhysicianId}
        />
      </Modal>
    </>
  );
};

export default FourthSection;
